import { hydrateOnInteraction, hydrateOnVisible } from 'vue'
import { DEFAULT_CONTENTS, EMPTY_OBJECT } from './const'
import { DEFAULT_IMAGE_DATA, DEFAULT_IMAGE_PARAMS } from './image/const'
import { DEFAULT_SLIDER_DATA, DEFAULT_SLIDER_PARAMS } from './slider/const'
import { DEFAULT_ICON_DATA, DEFAULT_ICON_PARAMS } from './icon/const'
import { DEFAULT_HEADING_DATA, DEFAULT_HEADING_PARAMS } from './heading/const'
import { DEFAULT_EDITOR_DATA, DEFAULT_EDITOR_PARAMS } from './editor/const'
import { DEFAULT_SPACE_DATA, DEFAULT_SPACE_PARAMS } from './space/const'
import { DEFAULT_BUTTON_DATA, DEFAULT_BUTTON_PARAMS } from './button/const'
import { DEFAULT_DIVIDER_DATA, DEFAULT_DIVIDER_PARAMS } from './divider/const'
import {
  DEFAULT_GOOGLE_MAP_ADVANCED_MARKER_DATA,
  DEFAULT_GOOGLE_MAP_CHILDS,
  DEFAULT_GOOGLE_MAP_DATA,
  DEFAULT_GOOGLE_MAP_INFOBOX_DATA,
  DEFAULT_GOOGLE_MAP_PARAMS,
} from './google-map/const'
import { DEFAULT_PRODUCTS_GRID_DATA, DEFAULT_PRODUCTS_GRID_PARAMS } from './products/grid/const'
import { DEFAULT_GENERAL_SLIDE_DATA, DEFAULT_GENERAL_SLIDE_PARAMS } from './slide/general/const'
import { DEFAULT_POST_SLIDE_DATA, DEFAULT_POST_SLIDE_PARAMS } from './slide/post/const'
import { DEFAULT_GALLERY_DATA, DEFAULT_GALLERY_PARAMS } from './gallery/const'
import {
  DEFAULT_VIDEO_ADDITIONAL_DOCUMENTS,
  DEFAULT_VIDEO_DATA,
  DEFAULT_VIDEO_PARAMS,
} from './video/const'
import { DEFAULT_ARTICLES_LIST_DATA, DEFAULT_ARTICLES_LIST_PARAMS } from './articles/list/const'
import {
  DEFAULT_CATEGORIES_LIST_DATA,
  DEFAULT_CATEGORIES_LIST_PARAMS,
} from './categories-list/const'
import { DEFAULT_BRANCHES_LIST_DATA, DEFAULT_BRANCHES_LIST_PARAMS } from './branches-list/const'
import {
  DEFAULT_FAQ_CHILDS,
  DEFAULT_FAQ_DATA,
  DEFAULT_FAQ_ITEM_DATA,
  DEFAULT_FAQ_ITEM_PARAMS,
  DEFAULT_FAQ_PARAMS,
  FAQ_PARSE,
  FAQ_SEARCH,
} from './faq/const'

import { EDITOR_PARSE, GENERAL_SLIDE_PARSE, HEADING_PARSE, POST_SLIDE_PARSE } from './parseable'
import {
  EDITOR_SEARCH,
  GENERAL_SLIDE_SEARCH,
  HEADING_SEARCH,
  POST_SLIDE_SEARCH,
} from './searchable'
import type {
  ADD_BLOCKS,
  AdvancedBlocks,
  Block,
  BLOCK_TO_ADD,
  BlockChildsRegistry,
  BlockDataRegistry,
  BLOCKS_STYLES,
} from '../types/Blocks'
import { predefined } from './predefined'
import { PLUGIN_BRANCHES, PLUGIN_ESHOP } from '../modules/plugins/plugin-types'
import type { PluginRequirement } from '../modules/plugins/plugin-types'

export class Blocks {
  private static instance: Blocks
  private customLabels: { [key: string]: string } = {}
  private customPlugins: { [key: string]: PluginRequirement[] } = {}
  private customIcons = ref<{ [key: string]: () => Promise<typeof import('*?inline')> }>({})
  private blockStyles: { [key in keyof BlockDataRegistry]?: BLOCKS_STYLES<key> } = {}
  private advancedComponentsView: { [key in AdvancedBlocks]?: Component } = {}
  private components: {
    [key: string]: {
      edit?: Component
      view?: Component
      icon?: Component
      inline?: Component
    }
  } = {
    placeholder: {
      view: defineAsyncComponent({
        loader: () => import('~~/shared-components/content/placeholder/PlaceholderView.vue'),
        hydrate: hydrateOnVisible(),
      }),
    },
    container: {
      edit: defineAsyncComponent({
        loader: () => import('~~/shared-components/content/container/ContainerEdit.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      view: defineAsyncComponent({
        loader: () => import('~~/shared-components/content/container/ContainerView.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconContainer.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
    },
    grid: {
      edit: defineAsyncComponent({
        loader: () => import('~~/shared-components/content/container/ContainerEdit.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      view: defineAsyncComponent({
        loader: () => import('~~/shared-components/content/container/ContainerView.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconContainer.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
    },
    heading: {
      edit: defineAsyncComponent({
        loader: () => import('./heading/HeadingEdit.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      view: defineAsyncComponent({
        loader: () => import('./heading/HeadingView.vue') as never,
        hydrate: () => {},
      }),
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconLetter.vue') as never,
        hydrate: () => {},
      }),
    },
    'categories-list': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/content/categories-list/CategoriesListIcon.vue'),
        hydrate: () => {},
      }),
      edit: defineAsyncComponent({
        loader: () => import('~~/shared-components/content/categories-list/CategoriesListEdit.vue'),
        hydrate: hydrateOnVisible(),
      }),
      view: defineAsyncComponent({
        loader: () => import('~~/shared-components/content/categories-list/CategoriesListView.vue'),
        hydrate: hydrateOnInteraction('mouseover'),
      }),
    },
    image: {
      edit: defineAsyncComponent({
        loader: () => import('./image/ImageEdit.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      view: defineAsyncComponent({
        loader: () => import('./image/ImageView.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconImage.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
    },
    text: {
      edit: defineAsyncComponent({
        loader: () => import('./editor/EditorEdit.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      inline: defineAsyncComponent({
        loader: () => import('./editor/EditorInline.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      view: defineAsyncComponent({
        loader: () => import('./editor/EditorView.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconText.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
    },
    button: {
      edit: defineAsyncComponent({
        loader: () => import('./button/ButtonEdit.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      view: defineAsyncComponent({
        loader: () => import('./button/ButtonView.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconButton.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
    },
    divider: {
      edit: defineAsyncComponent({
        loader: () => import('./divider/DividerEdit.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      view: defineAsyncComponent({
        loader: () => import('./divider/DividerView.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconDivider.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
    },
    space: {
      edit: defineAsyncComponent({
        loader: () => import('./space/SpaceEdit.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      view: defineAsyncComponent({
        loader: () => import('./space/SpaceView.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconSpacer.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
    },
    video: {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconVideo.vue') as never,
        hydrate: () => {},
      }),
      edit: defineAsyncComponent({
        loader: () => import('~~/shared-components/content/video/VideoEdit.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      view: defineAsyncComponent({
        loader: () => import('~~/shared-components/content/video/VideoView.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
    },
    'google-map': {
      edit: defineAsyncComponent(() => import('./google-map/GoogleMapEdit.vue') as never),
      view: defineAsyncComponent(() => import('./google-map/GoogleMapView.vue') as never),
      icon: defineAsyncComponent(
        () => import('~~/shared-components/icons/contents/IconGoogleMap.vue') as never
      ),
    },
    faq: {
      edit: defineAsyncComponent({
        loader: () => import('~~/shared-components/content/faq/FAQEdit.vue'),
        hydrate: hydrateOnVisible(),
      }),
      view: defineAsyncComponent({
        loader: () => import('~~/shared-components/content/faq/FAQView.vue'),
        hydrate: hydrateOnVisible(),
      }),
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/content/faq/FAQIcon.vue'),
        hydrate: () => {},
      }),
    },
    gallery: {
      edit: defineAsyncComponent({
        loader: () => import('./gallery/GalleryEdit.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      view: defineAsyncComponent({
        loader: () => import('./gallery/GalleryView.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconGallery.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
    },
    icon: {
      edit: defineAsyncComponent({
        loader: () => import('./icon/IconEdit.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      view: defineAsyncComponent({
        loader: () => import('./icon/IconView.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconFavourite.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
    },
    slider: {
      edit: defineAsyncComponent({
        loader: () => import('./slider/SliderEdit.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      view: defineAsyncComponent({
        loader: () => import('./slider/SliderView.vue') as never,
        hydrate: (hydrate, forEachElement) => {
          forEachElement(() => {
            hydrate()
          })
        },
      }),
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconCarousel.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
    },
    'products-grid': {
      edit: defineAsyncComponent({
        loader: () => import('./products/grid/ProductsGridEdit.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      view: defineAsyncComponent({
        loader: () => import('./products/grid/ProductsGridView.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconProductsGrid.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
    },
    'articles-list': {
      edit: defineAsyncComponent({
        loader: () => import('./articles/list/ArticlesListEdit.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
      view: defineAsyncComponent({
        loader: () => import('./articles/list/ArticlesListView.vue') as never,
        hydrate: hydrateOnInteraction('mouseover'),
      }),
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconArticlesList.vue') as never,
        hydrate: hydrateOnVisible(),
      }),
    },
    'general-slide': {
      icon: defineAsyncComponent({
        loader: () =>
          import('~~/shared-components/icons/contents/IconGeneralCarousel.vue') as never,
        hydrate: () => {},
      }),
    },
    'post-slide': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconPostCarousel.vue'),
        hydrate: () => {},
      }),
    },
    'branches-list': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/content/branches-list/BranchesListIcon.vue'),
        hydrate: () => {},
      }),
      edit: defineAsyncComponent({
        loader: () => import('~~/shared-components/content/branches-list/BranchesListEdit.vue'),
        hydrate: hydrateOnInteraction('mouseover'),
      }),
      view: defineAsyncComponent({
        loader: () => import('~~/shared-components/content/branches-list/BranchesListView.vue'),
        hydrate: hydrateOnVisible(),
      }),
    },
    'universal-container': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconContainer.vue'),
        hydrate: () => {},
      }),
    },
    'flex-3': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconFlex-3.vue'),
        hydrate: () => {},
      }),
    },
    'flex-4': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconFlex-4.vue'),
        hydrate: () => {},
      }),
    },
    'flex-5': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconFlex-5.vue'),
        hydrate: () => {},
      }),
    },
    'flex-6': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconFlex-6.vue'),
        hydrate: () => {},
      }),
    },
    'flex-7': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconFlex-7.vue'),
        hydrate: () => {},
      }),
    },
    'flex-8': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconFlex-8.vue'),
        hydrate: () => {},
      }),
    },
    'flex-9': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconFlex-9.vue'),
        hydrate: () => {},
      }),
    },
    'flex-10': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconFlex-10.vue'),
        hydrate: () => {},
      }),
    },
    'flex-11': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconFlex-11.vue'),
        hydrate: () => {},
      }),
    },
    'flex-12': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconFlex-12.vue'),
        hydrate: () => {},
      }),
    },
    'grid-1-2': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconGrid-1-2.vue'),
        hydrate: () => {},
      }),
    },
    'grid-2-1': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconGrid-2-1.vue'),
        hydrate: () => {},
      }),
    },
    'grid-3-1': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconGrid-3-1.vue'),
        hydrate: () => {},
      }),
    },
    'grid-1-3': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconGrid-1-3.vue'),
        hydrate: () => {},
      }),
    },
    'grid-2-2': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconGrid-2-2.vue'),
        hydrate: () => {},
      }),
    },
    'grid-3-2': {
      icon: defineAsyncComponent({
        loader: () => import('~~/shared-components/icons/contents/IconGrid-3-2.vue'),
        hydrate: () => {},
      }),
    },
  }

  public blocksChilds: {
    [key in keyof BlockChildsRegistry]?: {
      [chKey in BlockChildsRegistry[key]]?: BLOCK_TO_ADD
    }
  } = {
    slider: {
      'general-slide': {
        type: 'block',
        data: DEFAULT_GENERAL_SLIDE_DATA(),
        params: DEFAULT_GENERAL_SLIDE_PARAMS(),
        interactions: {},
        contents: DEFAULT_CONTENTS(),
        properties: {
          searchable: GENERAL_SLIDE_SEARCH(),
          parseable: GENERAL_SLIDE_PARSE(),
        },
        dynamicData: {},
      },
      'post-slide': {
        type: 'block',
        data: DEFAULT_POST_SLIDE_DATA(),
        params: DEFAULT_POST_SLIDE_PARAMS(),
        interactions: {},
        contents: DEFAULT_CONTENTS(),
        properties: {
          searchable: POST_SLIDE_SEARCH(),
          parseable: POST_SLIDE_PARSE(),
        },
        dynamicData: {},
      },
    },

    'google-map': {
      'advanced-marker': {
        type: 'block',
        data: DEFAULT_GOOGLE_MAP_ADVANCED_MARKER_DATA(),
        params: {},
        interactions: {},
        contents: DEFAULT_CONTENTS(),
      },
      // 'map-path': {
      //   type: 'block',
      //   data: (DEFAULT_GOOGLE_MAP_PATH_DATA),
      //   params: ({}),
      //   interactions: {},
      //   contents: (DEFAULT_CONTENTS),
      // },
      'map-infobox': {
        type: 'block',
        data: DEFAULT_GOOGLE_MAP_INFOBOX_DATA(),
        params: {},
        interactions: {},
        contents: DEFAULT_CONTENTS(),
      },
    },
    faq: {
      'faq-item': {
        type: 'block',
        params: DEFAULT_FAQ_ITEM_PARAMS(),
        data: DEFAULT_FAQ_ITEM_DATA(),
        interactions: {},
        properties: {
          searchable: FAQ_SEARCH(),
          parseable: FAQ_PARSE(),
        },
      },
    },
  }

  public blocks: {
    layout: { [key: string]: BLOCK_TO_ADD & { name: string } }
    general: { [key: string]: BLOCK_TO_ADD }
    advanced: { [key: string]: BLOCK_TO_ADD }
    custom: { [key: string]: BLOCK_TO_ADD }
  } = {
    layout: predefined.flex,
    general: {
      heading: {
        type: 'block',
        params: DEFAULT_HEADING_PARAMS(),
        data: DEFAULT_HEADING_DATA(),
        properties: {
          searchable: HEADING_SEARCH(),
          parseable: HEADING_PARSE(),
        },
      },
      image: {
        type: 'block',
        params: DEFAULT_IMAGE_PARAMS(),
        data: DEFAULT_IMAGE_DATA(),
        contents: DEFAULT_CONTENTS(),
        interactions: {},
      },
      text: {
        type: 'block',
        params: DEFAULT_EDITOR_PARAMS(),
        data: DEFAULT_EDITOR_DATA(),
        properties: {
          searchable: EDITOR_SEARCH(),
          parseable: EDITOR_PARSE(),
        },
      },
      video: {
        type: 'block',
        params: DEFAULT_VIDEO_PARAMS(),
        data: DEFAULT_VIDEO_DATA(),
        contents: DEFAULT_CONTENTS(),
        properties: {
          additionalDocuments: DEFAULT_VIDEO_ADDITIONAL_DOCUMENTS(),
        },
      },
      button: {
        type: 'block',
        params: DEFAULT_BUTTON_PARAMS(),
        interactions: {},
        data: DEFAULT_BUTTON_DATA(),
      },
      divider: {
        type: 'block',
        params: DEFAULT_DIVIDER_PARAMS(),
        data: DEFAULT_DIVIDER_DATA(),
        interactions: {},
      },
      space: {
        type: 'block',
        params: DEFAULT_SPACE_PARAMS(),
        data: DEFAULT_SPACE_DATA(),
      },
      'google-map': {
        type: 'block',
        params: DEFAULT_GOOGLE_MAP_PARAMS(),
        data: DEFAULT_GOOGLE_MAP_DATA(),
        children: DEFAULT_GOOGLE_MAP_CHILDS(),
      },
      icon: {
        type: 'block',
        params: DEFAULT_ICON_PARAMS(),
        data: DEFAULT_ICON_DATA(),
      },
      gallery: {
        type: 'block',
        params: DEFAULT_GALLERY_PARAMS(),
        data: DEFAULT_GALLERY_DATA(),
      },
    },
    advanced: {
      slider: {
        type: 'block',
        params: DEFAULT_SLIDER_PARAMS(),
        data: DEFAULT_SLIDER_DATA(),
        hasChildren: true,
        children: [
          this.blocksChilds.slider['general-slide']!,
          this.blocksChilds.slider['general-slide']!,
          this.blocksChilds.slider['general-slide']!,
        ],
      },
      faq: {
        type: 'block',
        params: DEFAULT_FAQ_PARAMS(),
        data: DEFAULT_FAQ_DATA(),
        children: DEFAULT_FAQ_CHILDS(),
      },
      'products-grid': {
        type: 'block',
        params: DEFAULT_PRODUCTS_GRID_PARAMS(),
        data: DEFAULT_PRODUCTS_GRID_DATA(),
      },
      'articles-list': {
        type: 'block',
        params: DEFAULT_ARTICLES_LIST_PARAMS(),
        data: DEFAULT_ARTICLES_LIST_DATA(),
      },
      'categories-list': {
        type: 'block',
        params: DEFAULT_CATEGORIES_LIST_PARAMS(),
        data: DEFAULT_CATEGORIES_LIST_DATA(),
      },
      'branches-list': {
        type: 'block',
        params: DEFAULT_BRANCHES_LIST_PARAMS(),
        data: DEFAULT_BRANCHES_LIST_DATA(),
      },
    },
    custom: {},
  }

  private childComponents: {
    [K in keyof BlockChildsRegistry]?: {
      [key in BlockChildsRegistry[K]]?: {
        edit?: Component
        view: Component
        icon?: Component
      }
    }
  } = {
    slider: {
      'general-slide': {
        edit: defineAsyncComponent({
          loader: () =>
            import('@@/shared-components/content/slide/general/SlideGeneralEdit.vue') as never,
          hydrate: hydrateOnVisible(),
        }),
        view: defineAsyncComponent({
          loader: () =>
            import('@@/shared-components/content/slide/general/SlideGeneralView.vue') as never,
          hydrate: hydrateOnVisible(),
        }),
        icon: defineAsyncComponent({
          loader: () =>
            import('~~/shared-components/icons/contents/IconGeneralCarousel.vue') as never,
          hydrate: () => {},
        }),
      },
      'post-slide': {
        edit: defineAsyncComponent({
          loader: () => import('@@/shared-components/content/slide/post/SlidePostEdit.vue'),
          hydrate: hydrateOnVisible(),
        }),
        view: defineAsyncComponent({
          loader: () => import('@@/shared-components/content/slide/post/SlidePostView.vue'),
          hydrate: hydrateOnVisible(),
        }),
        icon: defineAsyncComponent({
          loader: () => import('~~/shared-components/icons/contents/IconPostCarousel.vue'),
          hydrate: () => {},
        }),
      },
    },
    'google-map': {
      'map-path': {
        edit: defineAsyncComponent({
          loader: () =>
            import('@@/shared-components/content/google-map/child/components/path/PathEdit.vue'),
          hydrate: hydrateOnVisible(),
        }),
        view: defineAsyncComponent({
          loader: () =>
            import('@@/shared-components/content/google-map/child/components/path/PathView.vue'),
          hydrate: hydrateOnVisible(),
        }),
        icon: defineAsyncComponent({
          loader: () => import('@@/shared-components/icons/contents/IconGpsRoute.vue'),
          hydrate: () => {},
        }),
      },
      'advanced-marker': {
        edit: defineAsyncComponent({
          loader: () =>
            import(
              '~~/shared-components/content/google-map/child/components/advancedMarker/AdvancedMarkerEdit.vue'
            ),
          hydrate: hydrateOnVisible(),
        }),
        view: defineAsyncComponent({
          loader: () =>
            import(
              '@@/shared-components/content/google-map/child/components/advancedMarker/AdvancedMarkerView.vue'
            ),
          hydrate: hydrateOnVisible(),
        }),
        icon: defineAsyncComponent({
          loader: () => import('@@/shared-components/icons/contents/IconMarker.vue'),
          hydrate: () => {},
        }),
      },
      'map-infobox': {
        view: defineAsyncComponent({
          loader: () =>
            import(
              '@@/shared-components/content/google-map/child/components/infobox/InfoboxView.vue'
            ),
          hydrate: hydrateOnVisible(),
        }),
        edit: defineAsyncComponent({
          loader: () =>
            import(
              '@@/shared-components/content/google-map/child/components/infobox/InfoboxEdit.vue'
            ),
          hydrate: hydrateOnVisible(),
        }),
        icon: defineAsyncComponent({
          loader: () => import('@@/shared-components/icons/contents/IconInfobox.vue'),
          hydrate: () => {},
        }),
      },
    },
    faq: {
      'faq-item': {
        view: defineAsyncComponent({
          loader: () => import('@@/shared-components/content/faq/item/FAQItemSingleWrapper.vue'),
          hydrate: hydrateOnVisible(),
        }),
        icon: defineAsyncComponent({
          loader: () => import('~~/shared-components/content/faq/FAQIcon.vue'),
          hydrate: () => {},
        }),
      },
    },
  }

  private constructor() {}

  public static getInstance(): Blocks {
    if (!Blocks.instance) {
      Blocks.instance = new Blocks()
    }
    return Blocks.instance
  }

  public addBlocksChilds(blocks: {
    [key in keyof BlockChildsRegistry]?: {
      [chKey in BlockChildsRegistry[key]]?: BLOCK_TO_ADD
    }
  }) {
    Object.entries(blocks).forEach(([parent, childs]) => {
      const parentWithType = parent as keyof BlockChildsRegistry
      if (!this.blocksChilds[parentWithType]) {
        this.blocksChilds[parentWithType] = {}
      }
      Object.entries(childs).forEach(([childKey, childValue]) => {
        this.blocksChilds[parentWithType][childKey] = childValue
      })
    })
  }

  public addBlocks(blocks: ADD_BLOCKS) {
    const addBlocks = (blcks: BLOCK_TO_ADD[]) => {
      blcks.forEach((b) => {
        if (this.components[b.data.type]) {
          console.warn(
            'This block with this key already exists, if you need to overwrite the block, use the method `overwriteBlock`'
          )
          return
        }
        const block: Omit<Block, 'key'> = {
          type: 'block',
          params: b.params,
          data: b.data,
          interactions: b.interactions,
          dynamicData: b.dynamicData,
          children: b.children ?? [],
          properties: b.properties,
        }
        this.blocks.custom[b.data.type] = block
        this.components[b.data.type] = b.components
      })
    }
    addBlocks(Object.values(blocks))
  }

  public setAdvancedBlocks(blocks: { [key in AdvancedBlocks]?: Component }) {
    this.advancedComponentsView = blocks
  }

  public getAdvancedBlocksView(type: AdvancedBlocks) {
    return this.advancedComponentsView[type]
  }

  public overwriteBlock(
    blockKey: string,
    componentType: 'edit' | 'view' | 'inline',
    component: Component
  ): Component {
    if (!this.components[blockKey]) {
      throw new Error('This component with this key not exists')
    }
    return (this.components[blockKey][componentType] = component)
  }

  public overwriteAllComponentsOfBlock(
    blockKey: string,
    components: {
      edit: Component
      view: Component
      icon: Component
      inline?: Component
    }
  ): { edit: Component; view: Component; inline?: Component } {
    if (!this.components[blockKey]) {
      throw new Error('This component with this key not exists')
    }
    return (this.components[blockKey] = components)
  }

  public getComponents() {
    return markRaw(this.components)
  }

  public setChildComponents(blocks: {
    [key in keyof BlockChildsRegistry]?: {
      [childKey in BlockChildsRegistry[key]]?: {
        view?: Component
        edit?: Component
        inline?: Component
        icon?: Component
      }
    }
  }) {
    Object.entries(blocks).forEach(([parent, childs]) => {
      const parentWithType = parent as keyof BlockChildsRegistry
      if (!this.childComponents[parentWithType]) {
        this.childComponents[parentWithType] = {}
      }
      Object.entries(childs).forEach(([childKey, childValue]) => {
        this.childComponents[parentWithType][childKey] = childValue
      })
    })
  }

  public getBlocksChilds() {
    return this.blocksChilds
  }

  public getChildComponents(type: keyof BlockChildsRegistry) {
    return markRaw(this.childComponents[type] ?? EMPTY_OBJECT)
  }

  public getChildsComponents() {
    return markRaw(this.childComponents)
  }

  public addLabels(labels: { [key: string]: string }) {
    this.customLabels = {
      ...labels,
    }
  }

  public addPlugins(plugins: { [key: string]: PluginRequirement[] }) {
    this.customPlugins = {
      ...plugins,
    }
  }

  public addIcons(icons: { [key: string]: () => Promise<typeof import('*?inline')> }) {
    this.customIcons.value = {
      ...icons,
    }
  }

  public icons() {
    return this.customIcons
  }

  public labels() {
    const { t } = useI18n()
    return computed(() => ({
      'general-slide': t('Obecný snímek'),
      'post-slide': t('Snímek příspěvku'),
      'advanced-marker': t('Značka'),
      'map-path': t('Cesta'),
      'map-infobox': t('Infobox'),
      container: t('Kontejner'),
      grid: t('Mřížka'),
      heading: t('Nadpis'),
      image: t('Obrázek'),
      text: t('Textový editor'),
      video: t('Video'),
      button: t('Tlačítko'),
      divider: t('Oddělovač'),
      space: t('Mezera'),
      'google-map': t('Google mapa'),
      icon: t('Ikona'),
      slider: t('Posuvník'),
      gallery: t('Galerie'),
      'products-grid': t('Seznam produktů'),
      'articles-list': t('Seznam článku'),
      placeholder: t('Zástupce'),
      'universal-container': t('Univerzální kontejner'),
      'categories-list': t('Seznam kategorií'),
      'branches-list': t('Seznam poboček'),
      faq: t('Často kladené otázky'),
      'faq-item': t('Otázka'),
      ...this.customLabels,
    }))
  }

  // Dostupnost bloků podle projektových pluginů (plugins.json). Katalogová
  // metadata — záměrně mimo `properties`, aby se neukládala do obsahu v DB.
  public plugins(): { [key: string]: PluginRequirement[] } {
    return {
      'products-grid': [PLUGIN_ESHOP],
      'branches-list': [PLUGIN_BRANCHES],
      ...this.customPlugins,
    }
  }

  public addBlocksStyles(styles: { [key in keyof BlockDataRegistry]?: BLOCKS_STYLES<key> }) {
    this.blockStyles = { ...this.blockStyles, ...styles }
  }

  public getBlocksStyles(type: keyof BlockDataRegistry) {
    return this.blockStyles[type]
  }

  public getBlocks() {
    const result: { [key: string]: Block } = {}
    const collectBlocksByType = (input: Block[], result: { [key: string]: Block } = {}) => {
      for (const key in input) {
        const block = input[key]!
        // Získáme typ aktuálního bloku
        const type: string = block.type === 'block' ? block.data?.type : block.type
        // Pokud má aktuální blok další bloky (items), rekurzivně je projdeme
        const items = block.children
        if (items) {
          collectBlocksByType(items, result)
        }
        result[type] = block
      }

      return result
    }
    collectBlocksByType(
      Object.fromEntries([
        ...Object.entries(this.blocks.layout),
        ...Object.entries(this.blocks.general),
        ...Object.entries(this.blocks.advanced),
        ...Object.entries(this.blocks.custom),
      ]),
      result
    )
    return computed(() => result)
  }
}
